import React from 'react';
import type { DrawerT } from '../../../../../common/multi-level-drawer';
import type { AnnotationLabelT, RangeSpanT } from '../../../../../common/types/types';
import type { SidePanelDispatchAtom } from '../../../../../modernization-common/side-panel';
import type { ModernizedGenericAnnotation } from '../../../../../modernization-common/side-panel';
import { ANNOTATION_TYPE } from '../../../../labelling/constants';
import type { AnnotationT } from '../../../../labelling/types';
import type { SelectedAnnotationDetailsT } from '../../side-panel/types';
import { type EditAnnotationsPanelPropsT, EditAnnotationsPanel } from '../edit-annotation-panel/edit-annotation-panel';
import { HeaderAnnotationIcon, HeaderLabelModification } from '../edit-annotation-panel/label-modification';
import { FrameClassificationPanel } from '../frame-classification-panel/frame-classification-panel';
type PanelConfigContextT = {
  selectedId: string;
  annotation: ModernizedGenericAnnotation & {
    id: string;
  };
  isVideoTask: boolean;
  rangeSpan?: RangeSpanT;
  isEditButtonClicked: boolean;
  annotationName: string;
  selectedAnnotationDetails: SelectedAnnotationDetailsT;
  annotationLabels: AnnotationLabelT[];
  handleClose: () => void;
  sidePanelDispatchAtom: SidePanelDispatchAtom<AnnotationT & ModernizedGenericAnnotation>;
};
type PanelConfigT = {
  id: string;
  shouldOpen: (context: PanelConfigContextT) => boolean;
  priority: number;
  createDrawer: (context: PanelConfigContextT) => DrawerT;
};
const DRAWER_PANEL_CONFIGS: PanelConfigT[] = [{
  id: 'frame-classification-panel',
  priority: 0,
  shouldOpen: context => context.annotation.annotationType === ANNOTATION_TYPE.FRAME_CLASSIFICATION,
  createDrawer: (context): DrawerT => ({
    id: `frame-classification-${context.selectedId}`,
    title: context.annotation.annotationLabel.title,
    description: `Describe the ${context.annotation.annotationLabel.title}`,
    styleOverrides: {
      content: {
        padding: '0px'
      }
    },
    content: () => <FrameClassificationPanel annotationLabel={context.annotation.annotationLabel} annotationId={context.selectedId} existingSpan={context.rangeSpan} existingAnswers={context.annotation.metadata?.answers} dispatchAtom={context.sidePanelDispatchAtom} onClose={context.handleClose} />,
    onClose: context.handleClose
  })
}, {
  id: 'edit-panel',
  priority: 1,
  // Keep M4 aligned with legacy behavior: opening annotation details should always open edit panel.
  shouldOpen: () => true,
  createDrawer: (context): DrawerT<EditAnnotationsPanelPropsT> => ({
    id: `edit-${context.selectedId}`,
    title: context.annotationName,
    titleStartEnhancer: <HeaderAnnotationIcon />,
    titleEndEnhancer: <HeaderLabelModification selectedAnnotationDetails={context.selectedAnnotationDetails} annotationLabels={context.annotationLabels} dispatchAtom={context.sidePanelDispatchAtom} />,
    styleOverrides: {
      content: {
        padding: '0px'
      }
    },
    content: ({
      onClose,
      props
    }) => <EditAnnotationsPanel onClose={onClose} {...props} dispatchAtom={context.sidePanelDispatchAtom} />,
    onClose: context.handleClose
  })
}];
export function selectPanelToOpen(context: PanelConfigContextT): DrawerT | null {
  const sortedConfigs = [...DRAWER_PANEL_CONFIGS].sort((a, b) => a.priority - b.priority);
  for (const config of sortedConfigs) {
    if (config.shouldOpen(context)) {
      return config.createDrawer(context);
    }
  }
  return null;
}